import React, { useState, useEffect, useCallback } from 'react';
import './AdminOrders.css';

function AdminOrders({ token, userRole, onNavigate }) {
  const isPrivileged = userRole === 'admin' || userRole === 'supervisor';
  const [activeTab, setActiveTab] = useState('orders');

  // ===== ORDERS TAB STATE =====
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // ===== MANUAL MATCHES TAB STATE =====
  const [manualMatches, setManualMatches] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualDateFrom, setManualDateFrom] = useState('');
  const [manualDateTo, setManualDateTo] = useState('');
  const [manualPage, setManualPage] = useState(1);
  const [manualTotalPages, setManualTotalPages] = useState(1);
  const [manualTotalCount, setManualTotalCount] = useState(0);

  // ===== UNMATCHED TAB STATE =====
  const [unmatchedItems, setUnmatchedItems] = useState([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [unmatchedDateFrom, setUnmatchedDateFrom] = useState('');
  const [unmatchedDateTo, setUnmatchedDateTo] = useState('');
  const [unmatchedPage, setUnmatchedPage] = useState(1);
  const [unmatchedTotalPages, setUnmatchedTotalPages] = useState(1);
  const [unmatchedTotalCount, setUnmatchedTotalCount] = useState(0);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [clearingUnmatched, setClearingUnmatched] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Загрузка списка пользователей (только для admin/supervisor)
  useEffect(() => {
    if (!isPrivileged) return;
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/users-list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data);
        }
      } catch (err) {}
    };
    fetchUsers();
  }, [API_URL, token, isPrivileged]);

  // ===== ORDERS TAB =====
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      if (orderIdFilter) params.append('order_id', orderIdFilter);
      if (selectedUserId) params.append('user_id', selectedUserId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await fetch(`${API_URL}/api/admin/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load orders');
      const data = await response.json();
      setOrders(data.orders);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);
    } catch (err) {
      setError('Ошибка загрузки заказов');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, page, pageSize, orderIdFilter, selectedUserId, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
  }, [fetchOrders, activeTab]);

  const loadOrderDetails = async (orderId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrderDetails(data);
        setSelectedOrder(orderId);
      }
    } catch (err) {}
    finally { setLoadingDetails(false); }
  };

  const resetOrderFilters = () => {
    setSelectedUserId('');
    setDateFrom('');
    setDateTo('');
    setOrderIdFilter('');
    setPage(1);
  };

  // ===== MANUAL MATCHES TAB =====
  const fetchManualMatches = useCallback(async () => {
    setManualLoading(true);
    try {
      const params = new URLSearchParams({ page: manualPage.toString(), page_size: '20' });
      if (manualDateFrom) params.append('date_from', manualDateFrom);
      if (manualDateTo) params.append('date_to', manualDateTo);

      const response = await fetch(`${API_URL}/api/admin/manual-matches?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setManualMatches(data.items);
        setManualTotalPages(data.pagination.total_pages);
        setManualTotalCount(data.pagination.total_count);
      }
    } catch (err) {}
    finally { setManualLoading(false); }
  }, [API_URL, token, manualPage, manualDateFrom, manualDateTo]);

  useEffect(() => {
    if (activeTab === 'manual') fetchManualMatches();
  }, [fetchManualMatches, activeTab]);

  const handleDeleteFromLearning = async (item) => {
    if (!window.confirm('Удалить позицию из обучения?')) return;
    try {
      // Use learning_id (LearningData.id) if available for direct deletion
      const url = item.learning_id
        ? `${API_URL}/api/settings/fine-tune/examples/${item.learning_id}`
        : `${API_URL}/api/matching/${item.id}/learning`;
      const opts = item.learning_id
        ? { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        : { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ save_to_learning: false }) };
      const response = await fetch(url, opts);
      if (response.ok) {
        setManualMatches(prev => prev.filter(m => m.id !== item.id));
        setManualTotalCount(prev => prev - 1);
      }
    } catch (err) {}
  };

  // ===== UNMATCHED TAB =====
  const fetchUnmatched = useCallback(async () => {
    setUnmatchedLoading(true);
    try {
      const params = new URLSearchParams({ page: unmatchedPage.toString(), page_size: '20' });
      if (unmatchedDateFrom) params.append('date_from', unmatchedDateFrom);
      if (unmatchedDateTo) params.append('date_to', unmatchedDateTo);

      const response = await fetch(`${API_URL}/api/admin/unmatched?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUnmatchedItems(data.items);
        setUnmatchedTotalPages(data.pagination.total_pages);
        setUnmatchedTotalCount(data.pagination.total_count);
      }
    } catch (err) {}
    finally { setUnmatchedLoading(false); }
  }, [API_URL, token, unmatchedPage, unmatchedDateFrom, unmatchedDateTo]);

  useEffect(() => {
    if (activeTab === 'unmatched') fetchUnmatched();
  }, [fetchUnmatched, activeTab]);

  const handleExportUnmatched = () => {
    const params = new URLSearchParams();
    if (unmatchedDateFrom) params.append('date_from', unmatchedDateFrom);
    if (unmatchedDateTo) params.append('date_to', unmatchedDateTo);

    fetch(`${API_URL}/api/admin/unmatched/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => response.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'unmatched_positions.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(err => alert('Ошибка при скачивании: ' + err.message));
  };

  const handleExportAndClearHistory = () => {
    if (!window.confirm(
      `Вы уверены? Все заказы из истории будут удалены.\n\n` +
      `Перед удалением будет скачан Excel-файл со всей историей.\n` +
      `Данные для машинного обучения (learning data) сохранятся.`
    )) return;

    setClearingHistory(true);
    fetch(`${API_URL}/api/admin/export-and-clear-history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => {
        if (!response.ok) throw new Error('Ошибка сервера');
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        link.setAttribute('download', `orders_history_${date}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setOrders([]);
        setTotalCount(0);
        setTotalPages(1);
      })
      .catch(err => alert('Ошибка при очистке истории: ' + err.message))
      .finally(() => setClearingHistory(false));
  };

  const handleClearUnmatched = async () => {
    if (!window.confirm(
      `Вы уверены? Все несопоставленные позиции будут удалены.\n` +
      `Данные для машинного обучения (learning data) сохранятся.`
    )) return;

    setClearingUnmatched(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/clear-unmatched`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Ошибка сервера');
      const data = await response.json();
      alert(`Удалено несопоставленных позиций: ${data.deleted}`);
      setUnmatchedItems([]);
      setUnmatchedTotalCount(0);
      setUnmatchedTotalPages(1);
    } catch (err) {
      alert('Ошибка при очистке: ' + err.message);
    } finally {
      setClearingUnmatched(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="admin-orders">
      <div className="admin-orders-header">
        <h2>История</h2>
      </div>

      {/* Вкладки */}
      <div className="history-tabs">
        <button
          className={`history-tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Заказы
          {activeTab === 'orders' && totalCount > 0 && (
            <span className="tab-count">{totalCount}</span>
          )}
        </button>
        {isPrivileged && (
          <>
            <button
              className={`history-tab-btn ${activeTab === 'manual' ? 'active' : ''}`}
              onClick={() => setActiveTab('manual')}
            >
              Ручное сопоставление
              {activeTab === 'manual' && manualTotalCount > 0 && (
                <span className="tab-count">{manualTotalCount}</span>
              )}
            </button>
            <button
              className={`history-tab-btn ${activeTab === 'unmatched' ? 'active' : ''}`}
              onClick={() => setActiveTab('unmatched')}
            >
              Несопоставленные позиции
              {activeTab === 'unmatched' && unmatchedTotalCount > 0 && (
                <span className="tab-count">{unmatchedTotalCount}</span>
              )}
            </button>
          </>
        )}
      </div>

      {/* ===== ВКЛАДКА: ЗАКАЗЫ ===== */}
      {activeTab === 'orders' && (
        <>
          <div className="filters-section">
            <div className="filter-group">
              <label>№ заказа:</label>
              <input
                type="number"
                placeholder="Например: 205"
                value={orderIdFilter}
                onChange={(e) => setOrderIdFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchOrders(); } }}
                style={{ width: '120px' }}
              />
            </div>
            {isPrivileged && (
              <div className="filter-group">
                <label>Пользователь:</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">Все пользователи</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.email}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="filter-group">
              <label>Дата от:</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>Дата до:</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="filter-buttons">
              <button className="apply-btn" onClick={() => { setPage(1); fetchOrders(); }}>
                Применить
              </button>
              <button className="reset-btn" onClick={resetOrderFilters}>
                Сбросить
              </button>
            </div>
          </div>

          <div className="tab-total-row">
            <span className="tab-total-text">Всего: {totalCount}</span>
            {userRole === 'admin' && (
              <button
                className="clear-history-btn"
                onClick={handleExportAndClearHistory}
                disabled={clearingHistory || totalCount === 0}
              >
                {clearingHistory ? 'Скачивание и очистка...' : '🗑 Очистить историю и скачать в файл'}
              </button>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">Загрузка...</div>
          ) : (
            <>
              <div className="orders-table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      {isPrivileged && <th>Пользователь</th>}
                      <th>Позиций</th>
                      <th>Статус</th>
                      <th>Дата</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan={isPrivileged ? 6 : 5} className="no-data">Нет заказов</td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr
                          key={order.id}
                          className={selectedOrder === order.id ? 'selected' : ''}
                        >
                          <td>{order.id}</td>
                          {isPrivileged && <td>{order.user_email}</td>}
                          <td>{order.items_count}</td>
                          <td>
                            <span className={`status-badge ${order.status}`}>
                              {order.status === 'completed' ? 'Завершен' : 'В обработке'}
                            </span>
                          </td>
                          <td>{formatDate(order.created_at)}</td>
                          <td>
                            <button
                              className="view-btn"
                              onClick={() => loadOrderDetails(order.id)}
                            >
                              Детали
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    &laquo; Назад
                  </button>
                  <span className="page-info">Страница {page} из {totalPages}</span>
                  <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                    Вперед &raquo;
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ===== ВКЛАДКА: РУЧНОЕ СОПОСТАВЛЕНИЕ ===== */}
      {activeTab === 'manual' && (
        <>
          <div className="filters-section">
            <div className="filter-group">
              <label>Дата от:</label>
              <input
                type="date"
                value={manualDateFrom}
                onChange={(e) => setManualDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Дата до:</label>
              <input
                type="date"
                value={manualDateTo}
                onChange={(e) => setManualDateTo(e.target.value)}
              />
            </div>
            <div className="filter-buttons">
              <button className="apply-btn" onClick={() => { setManualPage(1); fetchManualMatches(); }}>
                Применить
              </button>
              <button className="reset-btn" onClick={() => {
                setManualDateFrom(''); setManualDateTo(''); setManualPage(1);
              }}>
                Сбросить
              </button>
            </div>
          </div>

          <div className="tab-total">Всего: {manualTotalCount}</div>

          {manualLoading ? (
            <div className="loading">Загрузка...</div>
          ) : (
            <>
              <div className="orders-table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Позиция из заказа</th>
                      <th>Сопоставлено с</th>
                      <th>Кол-во</th>
                      <th>Пользователь</th>
                      <th>Дата</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualMatches.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="no-data">Нет данных</td>
                      </tr>
                    ) : (
                      manualMatches.map((item, index) => (
                        <tr key={item.id}>
                          <td>{(manualPage - 1) * 20 + index + 1}</td>
                          <td>{item.order_name}</td>
                          <td>{item.matched_name}</td>
                          <td>{item.quantity}</td>
                          <td>{item.user_email}</td>
                          <td>{formatDate(item.created_at)}</td>
                          <td>
                            <button
                              className="delete-learning-btn"
                              onClick={() => handleDeleteFromLearning(item)}
                            >
                              Удалить
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {manualTotalPages > 1 && (
                <div className="pagination">
                  <button disabled={manualPage === 1} onClick={() => setManualPage((p) => p - 1)}>
                    &laquo; Назад
                  </button>
                  <span className="page-info">Страница {manualPage} из {manualTotalPages}</span>
                  <button disabled={manualPage === manualTotalPages} onClick={() => setManualPage((p) => p + 1)}>
                    Вперед &raquo;
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ===== ВКЛАДКА: НЕСОПОСТАВЛЕННЫЕ ===== */}
      {activeTab === 'unmatched' && (
        <>
          <div className="filters-section">
            <div className="filter-group">
              <label>Дата от:</label>
              <input
                type="date"
                value={unmatchedDateFrom}
                onChange={(e) => setUnmatchedDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Дата до:</label>
              <input
                type="date"
                value={unmatchedDateTo}
                onChange={(e) => setUnmatchedDateTo(e.target.value)}
              />
            </div>
            <div className="filter-buttons">
              <button className="apply-btn" onClick={() => { setUnmatchedPage(1); fetchUnmatched(); }}>
                Применить
              </button>
              <button className="reset-btn" onClick={() => {
                setUnmatchedDateFrom(''); setUnmatchedDateTo(''); setUnmatchedPage(1);
              }}>
                Сбросить
              </button>
            </div>
          </div>

          <div className="tab-total-row">
            <span className="tab-total-text">Всего: {unmatchedTotalCount}</span>
            <div className="tab-actions">
              <button className="export-unmatched-btn" onClick={handleExportUnmatched}>
                📥 Скачать Excel
              </button>
              {userRole === 'admin' && (
                <button
                  className="clear-unmatched-btn"
                  onClick={handleClearUnmatched}
                  disabled={clearingUnmatched || unmatchedTotalCount === 0}
                >
                  {clearingUnmatched ? 'Очистка...' : '🗑 Очистить несопоставленные'}
                </button>
              )}
            </div>
          </div>

          {unmatchedLoading ? (
            <div className="loading">Загрузка...</div>
          ) : (
            <>
              <div className="orders-table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Позиция из заказа</th>
                      <th>Кол-во</th>
                      <th>Пользователь</th>
                      <th>Дата</th>
                      <th>ID заказа</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedItems.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-data">Нет несопоставленных позиций</td>
                      </tr>
                    ) : (
                      unmatchedItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>{(unmatchedPage - 1) * 20 + index + 1}</td>
                          <td>{item.order_name}</td>
                          <td>{item.quantity}</td>
                          <td>{item.user_email}</td>
                          <td>{formatDate(item.created_at)}</td>
                          <td>#{item.order_id}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {unmatchedTotalPages > 1 && (
                <div className="pagination">
                  <button disabled={unmatchedPage === 1} onClick={() => setUnmatchedPage((p) => p - 1)}>
                    &laquo; Назад
                  </button>
                  <span className="page-info">Страница {unmatchedPage} из {unmatchedTotalPages}</span>
                  <button disabled={unmatchedPage === unmatchedTotalPages} onClick={() => setUnmatchedPage((p) => p + 1)}>
                    Вперед &raquo;
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Модальное окно деталей заказа */}
      {selectedOrder && (
        <div className="order-details-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Заказ #{selectedOrder}</h3>
              <button
                className="close-btn"
                onClick={() => { setSelectedOrder(null); setOrderDetails(null); }}
              >
                &times;
              </button>
            </div>

            {loadingDetails ? (
              <div className="loading">Загрузка...</div>
            ) : orderDetails ? (
              <div className="modal-body">
                <div className="order-info">
                  <p><strong>Пользователь:</strong> {orderDetails.order.user_email}</p>
                  <p><strong>Дата:</strong> {formatDate(orderDetails.order.created_at)}</p>
                  <p><strong>Файл номенклатуры:</strong> {orderDetails.order.nomenclature_file || '-'}</p>
                  {orderDetails.order.ai_comment && (
                    <p><strong>Комментарий:</strong> {orderDetails.order.ai_comment}</p>
                  )}
                </div>

                <h4>Результаты сопоставления ({orderDetails.results.length})</h4>
                <div className="results-list">
                  {orderDetails.results.map((result) => (
                    <div key={result.id} className="result-item">
                      <div className="result-order-name">{result.order_name}</div>
                      <div className="result-matched">
                        {result.matched_name ? (
                          <span className="matched">{result.matched_name}</span>
                        ) : (
                          <span className="not-matched">Не сопоставлено</span>
                        )}
                      </div>
                      <div className="result-quantity">x{result.quantity}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
